import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { FilterBuilderComponent } from '../../filter/component/filter-builder/filter-builder.component';
import { SetOfFiltersComponent } from '../../filter/component/set-of-filters/set-of-filters.component';
import { DataSource } from '../../model/data-source';
import { DataOptionsConnectorService } from '../../service/data-options-connector.service';
import { RequestCriteria } from '../../model/request-criteria';
import { FilterHistoryComponent } from '../../filter/component/filter-history/filter-history.component';

@Component({
  selector: 'app-data-options',
  templateUrl: './data-options.component.html',
  styleUrls: ['./data-options.component.scss']
})
export class DataOptionsComponent implements OnInit, AfterViewInit, OnDestroy {

  readonly historyClientId = 'filter-builder';

  @ViewChild('filterBuilder') filterBuilder: FilterBuilderComponent;
  @ViewChild('setOfFilters') setOfFiltersComponent: SetOfFiltersComponent;
  @ViewChild('filterHistory') filterHistory: FilterHistoryComponent;

  @Input()
  noDataSourceMsgKey: string;
  @Input()
  setOfFiltersGroupNames = ['readOnlyFilters', 'generalPurposeFilters', 'userFilters'];

  @Input()
  showLexicografically: boolean;

  @Input()
  showFilterButton: boolean = true;

  @Input()
  selectedRows: any[] | null = null;

  @Output()
  readonly validityChange = new EventEmitter<boolean>();

  private dataOptionsConnectorSubscription?: Subscription;
  private builderChangeSub: Subscription;

  dataSource: DataSource;
  loading: boolean;

  constructor(public dataOptionsConnectorService: DataOptionsConnectorService, private changeDetector: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.dataOptionsConnectorSubscription = this.dataOptionsConnectorService?.loading$.subscribe(loading => {
      this.loading = loading;
      this.changeDetector.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    this.builderChangeSub = this.filterBuilder.form.valueChanges.subscribe(() => {
      try {
        this.updateCurrentFilters();
      } catch (error) {
        // Wyjątek puszczony dalej zerwałby subskrypcję na stałe - panel przestałby wtedy bez słowa
        // zapisywać bieżące kryteria, a "Filtruj" odtwarzałoby formularz z ostatnio zapamiętanych.
        console.error('Failed to read filters from the form', error);
      }
      this.validityChange.emit(this.filterBuilder.form.valid);
    });
  }

  ngOnDestroy(): void {
    this.dataOptionsConnectorSubscription?.unsubscribe();
    this.builderChangeSub.unsubscribe();
  }

  setDataSource(dataSource: DataSource, force: boolean = false): void {
    this.dataSource = dataSource;
    this.changeDetector.detectChanges();
    this.filterBuilder.setFields(dataSource.getValueFields());
    this.filterHistory.init(dataSource, this.historyClientId, force);
    const history = this.filterHistory.getRecent();
    const initial = force ? dataSource.initialFilters : history ?? dataSource.initialFilters;
    this.filterBuilder.setInitialFilters(initial);
    this.filterBuilder.setFormValidators(dataSource.customValidators);
    this.setOfFiltersComponent.initSetsOfFilters(this.dataSource);
    this.updateCurrentFilters();
    const filterValid = this.filterBuilder.validate();
    this.validityChange.emit(filterValid);
    if (force && filterValid) {
      this.filterHistory.save();
    } else if (!filterValid) {
      this.dataOptionsConnectorService.updateFilters(null);
    }
    this.dataOptionsConnectorService.activateFilters();
    this.changeDetector.detectChanges();
  }


  filter(): void {
    if (this.filterBuilder.validate()) {
      this.dataOptionsConnectorService.activateFilters();
      this.filterHistory.save();
    }
  }

  cancel(): void {
    this.dataOptionsConnectorService.cancel();
  }

  historyNavigate(criteria: RequestCriteria): void {
    this.filterBuilder
      .setInitialFilters(criteria, true);
  }

  validate(): void {
    this.filterBuilder.form.updateValueAndValidity();
  }

  private updateCurrentFilters(): void {
    if (this.filterBuilder.validate()) {
      const criteria: RequestCriteria = this.filterBuilder.getFilters();
      this.dataOptionsConnectorService.getActiveFilters().subscribe((activeFilters: RequestCriteria) => {
        if (activeFilters?.criteria?.length)
          criteria.criteria?.push(...activeFilters.criteria?.filter(crit => crit.columnFilter));
      });
      this.filterHistory.put(this.filterBuilder.getFilters());
      this.dataOptionsConnectorService.updateFilters(criteria);
    }
  }
}
